import { inject, InjectionKey, provide } from 'vue'

type ProvideInject<T> = [(value: T) => void, () => T]

type NullableProvideInject<T> = [(value: T) => void, () => T | null]

function injectStrict<T>(key: InjectionKey<T>): T {
  const resolved = inject(key)

  if (resolved === undefined) {
    throw Error(
      `(provide/inject) '${key.description}' - injected value not provided`,
    )
  }

  return resolved
}

export function createInjectionKey<T>(key: string): InjectionKey<T> {
  return Symbol(key)
}

/**
 * Create a `provide`/`inject` pair with strict checking of the value.
 *
 * It requires `provide` to be called with a value before `inject` is called.
 *
 * - If the `provide` value is explicitly `undefined`, `inject` will throw an error.
 * - If `provide` is not invoked (so the value is implicitly `undefined`),
 *    `inject` will throw an error with an additional warning in the console from Vue.
 *
 * If some `provide` is optional but should still be explicitly called (to remind declaring an "important" dependency),
 * pass `null` as the value.
 *
 * If some `provide` is optional and can be omitted (to skip writing an unnecessary explicit `null`),
 * use `createNullableProvideInject` instead.
 *
 * @param name - name of the injection key (for debugging purposes; doesn't have to be unique)
 *
 * @returns provide and inject functions
 */
export function createProvideInject<T>(name: string): ProvideInject<T> {
  const key = createInjectionKey<T>(name)

  function provideValue(value: T): void {
    provide(key, value)
  }

  function injectValue(): T {
    return injectStrict(key)
  }

  return [provideValue, injectValue]
}

/**
 * Creates a `provide`/`inject` pair with an optional default value of `null`.
 *
 * This utility is useful when a provided value is optional.
 * If `provide` is not called, the corresponding `inject` will return `null`, instead of throwing an error.
 *
 * @param name - A descriptive name for the injection key (used for debugging; does not need to be unique).
 *
 * @returns An array containing the `provide` and `inject` functions.
 */
export function createNullableProvideInject<T>(
  name: string,
): NullableProvideInject<T> {
  const key = createInjectionKey<T>(name)

  function provideValue(value: T): void {
    provide(key, value)
  }

  function injectValue(): T | null {
    return inject(key, null) as T | null
  }

  return [provideValue, injectValue]
}
